import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CopingTool {
  id?: string;
  title: string;
  description: string;
  instructions?: string;
  category: string;
  tool_type: string;
  difficulty_level: string;
  duration_minutes?: number;
  tags: string[];
  icon_name?: string;
  is_published: boolean;
  sort_order: number;
}

export default function CopingToolsManager() {
  const [tools, setTools] = useState<CopingTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTool, setEditingTool] = useState<CopingTool | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const defaultTool: CopingTool = {
    title: '',
    description: '',
    instructions: '',
    category: 'general',
    tool_type: 'technique',
    difficulty_level: 'beginner',
    tags: [],
    is_published: true,
    sort_order: 0
  };

  useEffect(() => {
    loadTools();
  }, []);

  const loadTools = async () => {
    try {
      const { data, error } = await supabase
        .from('coping_tools')
        .select('*')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      setTools(data || []);
    } catch (error) {
      console.error('Error loading tools:', error);
      toast.error('Failed to load coping tools');
    } finally {
      setLoading(false);
    }
  };

  const saveTool = async (tool: CopingTool) => {
    try {
      if (tool.id) {
        const { error } = await supabase
          .from('coping_tools')
          .update(tool)
          .eq('id', tool.id);
        if (error) throw error;
        toast.success('Tool updated successfully');
      } else {
        const { error } = await supabase
          .from('coping_tools')
          .insert([tool]);
        if (error) throw error;
        toast.success('Tool created successfully');
      }
      
      loadTools();
      setEditingTool(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving tool:', error);
      toast.error('Failed to save tool');
    }
  };

  const deleteTool = async (id: string) => {
    try {
      const { error } = await supabase
        .from('coping_tools')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Tool deleted successfully');
      loadTools();
    } catch (error) {
      console.error('Error deleting tool:', error);
      toast.error('Failed to delete tool');
    }
  };

  const ToolForm = ({ tool, onSave, onCancel }: { 
    tool: CopingTool; 
    onSave: (tool: CopingTool) => void; 
    onCancel: () => void; 
  }) => {
    const [formData, setFormData] = useState(tool);

    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>{tool.id ? 'Edit Coping Tool' : 'Create New Coping Tool'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Deep Breathing Technique"
              />
            </div>
            <div>
              <Label htmlFor="icon_name">Icon Name (Lucide)</Label>
              <Input
                id="icon_name"
                value={formData.icon_name || ''}
                onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
                placeholder="Heart"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="A calming breathing technique to reduce anxiety"
              rows={3}
            />
          </div>
          
          <div>
            <Label htmlFor="instructions">Instructions</Label>
            <Textarea
              id="instructions"
              value={formData.instructions || ''}
              onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              placeholder="Step-by-step instructions for this coping tool"
              rows={4}
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="anxiety">Anxiety</SelectItem>
                  <SelectItem value="depression">Depression</SelectItem>
                  <SelectItem value="stress">Stress</SelectItem>
                  <SelectItem value="mindfulness">Mindfulness</SelectItem>
                  <SelectItem value="grounding">Grounding</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="tool_type">Tool Type</Label>
              <Select value={formData.tool_type} onValueChange={(value) => setFormData({ ...formData, tool_type: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technique">Technique</SelectItem>
                  <SelectItem value="exercise">Exercise</SelectItem>
                  <SelectItem value="resource">Resource</SelectItem>
                  <SelectItem value="activity">Activity</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="difficulty_level">Difficulty</Label>
              <Select value={formData.difficulty_level} onValueChange={(value) => setFormData({ ...formData, difficulty_level: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="duration_minutes">Duration (minutes)</Label>
              <Input
                id="duration_minutes"
                type="number"
                value={formData.duration_minutes || ''}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || undefined })}
                placeholder="5"
              />
            </div>
            
            <div>
              <Label htmlFor="sort_order">Sort Order</Label>
              <Input
                id="sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="tags">Tags (comma separated)</Label>
            <Input
              id="tags"
              value={formData.tags.join(', ')}
              onChange={(e) => setFormData({ 
                ...formData, 
                tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) 
              })}
              placeholder="breathing, relaxation, anxiety"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="is_published"
              checked={formData.is_published}
              onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
            />
            <Label htmlFor="is_published">Published</Label>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={() => onSave(formData)}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            <Button variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div>Loading coping tools...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Coping Tools Management</h2>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add New Tool
        </Button>
      </div>
      
      {isCreating && (
        <ToolForm
          tool={defaultTool}
          onSave={saveTool}
          onCancel={() => setIsCreating(false)}
        />
      )}
      
      {editingTool && (
        <ToolForm
          tool={editingTool}
          onSave={saveTool}
          onCancel={() => setEditingTool(null)}
        />
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tools.map((tool) => (
          <Card key={tool.id} className={!tool.is_published ? 'opacity-60' : ''}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{tool.title}</CardTitle>
                  <div className="flex gap-1 mt-1">
                    <Badge variant="secondary">{tool.category}</Badge>
                    <Badge variant="outline">{tool.tool_type}</Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingTool(tool)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => tool.id && deleteTool(tool.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-2">{tool.description}</p>
              
              <div className="space-y-1 text-xs text-muted-foreground">
                <div>Difficulty: {tool.difficulty_level}</div>
                {tool.duration_minutes && <div>Duration: {tool.duration_minutes} min</div>}
                <div>Sort: {tool.sort_order} | {tool.is_published ? 'Published' : 'Draft'}</div>
              </div>
              
              {tool.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {tool.tags.map((tag, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}